/**
 * Play → Domain route resolver (Three-Layer PRD §2.5 — the Dive-in resolver).
 *
 * Before the Domain layer landed, a Play action's `ctaHref` would drop the user
 * on an empty `?view=objects` grid. With Layer 2 restored (domains.ts), Play
 * routes into the REAL Domain view — never a generic table. This is the
 * client-side resolver (OQ-5: client-side for now; a server `play.route` op only
 * if shipping domains.ts to the client ever becomes a concern — it is pure data,
 * so it does not). Pure + isolation-testable; no React, no I/O.
 *
 *   resolvePlayRoute(action):
 *     1. an absolute in-app ctaHref is already correct → use it verbatim
 *        (Play "orchestrates, not rebuilds" — the engine sets correct deep links,
 *        and those now resolve to real Domain views because Layer 2 exists).
 *     2. else derive the object kind + uid from the action key/ctaHref; if the
 *        kind maps to a Domain, land in that Domain's home with the object focused.
 *     3. else a bare object → its /o/<uid> page.
 *     4. else the mode/Work fallback (never a dead end).
 */

import { domainForKind, domainHomeRoute } from "./domains";
import { MODE_SLUGS, modeForKind, type ModeSlug } from "./modes-registry";

/** Event-prefix → commitment kind (the bridge prefixes + a few aliases), so a
 *  Play action keyed `crm_opportunity:<uid>` resolves to the `deal` Domain. */
const PREFIX_KIND: Readonly<Record<string, string>> = {
  crm_opportunity: "deal",
  opportunity: "deal",
  deal: "deal",
  crm_lead: "lead",
  lead: "lead",
  hiring_application: "application",
  application: "application",
  hiring_job: "job",
  job: "job",
  hiring_scorecard: "scorecard",
  scorecard: "scorecard",
  task: "task",
  goal: "goal",
  run: "run",
  document: "document",
  competitor: "competitor",
  swot: "swot",
  icp: "icp",
};

/** Extract an object uid from a `/o/<uid>` href or a `<prefix>:<uid>` key.
 *
 *  Object uids are **26-char UPPERCASE alphanumeric** (`019E28C0…` — the migration
 *  `%012X || hex(randomblob(7))` format, and ULID Crockford base32). Play action
 *  KEYS, by contrast, are lowercase human slugs (`quickwin:stand-up-a-workflow`,
 *  `intel:commitment-risk:customer`). Matching the uid SHAPE — not just "a long
 *  token" — is what stops a slug being mistaken for a uid and routed to a 404
 *  `/o/<slug>` (the bug the earlier `\b[0-9A-Za-z]{20,}\b` fallback caused). An
 *  explicit `/o/<uid>` href is trusted verbatim (the path already asserts it). */
const UID_SHAPE = /([0-9A-Z]{26})\b/; // 26-char uppercase (hex or ULID base32)
export function extractUid(s: string | undefined | null): string | null {
  if (!s) return null;
  const o = s.match(/\/o\/([0-9A-Za-z]{16,})/);
  if (o) return o[1]!;
  // Require the uid shape, after a delimiter or standalone — never a lowercase slug.
  const delim = s.match(new RegExp(`[:#/=]${UID_SHAPE.source}`));
  if (delim) return delim[1]!;
  const bare = s.match(new RegExp(`\\b${UID_SHAPE.source}`));
  return bare ? bare[1]! : null;
}

export interface PlayRouteInput {
  key: string;
  ctaHref?: string;
  /** The object the action is ABOUT (§2.5). When present it takes precedence:
   *  `deepLink` is the engine's pre-resolved domain route; `uid`/`kind` enrich the
   *  derivation. Optional + back-compat — absent ⇒ the key/ctaHref derivation. */
  target?: { uid?: string; kind?: string; deepLink?: string };
}

/**
 * Resolve a Play action to a route. `opts.dive` selects the §2.5 **Dive-in** verb:
 * it explicitly opens the object's OWNING Domain view (skipping a raw `ctaHref`),
 * where the default (the **Do** verb) trusts an absolute `ctaHref` first. Both
 * honor `target` (the engine's pre-resolved deep-link wins for either verb).
 */
export function resolvePlayRoute(action: PlayRouteInput, opts?: { dive?: boolean }): string {
  const t = action.target;
  // 0. The engine's pre-resolved domain deep-link is canonical for BOTH verbs.
  if (t?.deepLink && t.deepLink.startsWith("/") && t.deepLink !== "/") {
    return t.deepLink;
  }
  // 1. Do (default): an absolute in-app ctaHref is canonical (and now lands in a
  //    real Domain view, since Layer 2 exists). Dive-in SKIPS this — it wants the
  //    object's owning Domain view, not the raw CTA. External/empty hrefs fall through.
  if (
    !opts?.dive &&
    action.ctaHref &&
    action.ctaHref.startsWith("/") &&
    action.ctaHref !== "/"
  ) {
    return action.ctaHref;
  }
  // 1b. Universal Intake footgun guard (INTAKE-PRD §2.8.1, belt two): an intake
  //     card's key is `intake:<batchUid>`, and a batchUid matches UID_SHAPE — so
  //     without this an href-less intake card would mis-route to `/o/<batchUid>`
  //     (step 3). Route to its absolute ctaHref (the batch drill) when present,
  //     else `/database` — NEVER the object page. Belt one is the gatherer always
  //     setting a ctaHref; this backstops a card that somehow lacks one.
  const prefix = (action.key.split(":")[0] ?? "").toLowerCase();
  if (prefix === "intake") {
    return action.ctaHref && action.ctaHref.startsWith("/") ? action.ctaHref : "/database";
  }
  // Learning Loop (WS3): a memory learning's card is keyed `learning:<id>`; a
  // skill-patch NOTIFY card is keyed `learning-skill:<id>` (the distinct prefix
  // keeps the memory-only "Keep this" affordances from matching it — see
  // routeSkillLearningsToTasks). Both route to the same learning settings page
  // rather than falling through to `/work` (a dead end, since neither key
  // carries an object uid or a Domain kind).
  if (prefix === "learning" || prefix === "learning-skill") {
    return "/settings/agents/learning";
  }
  // 2. Derive kind from target.kind or the key prefix; if it maps to a Domain, dive
  //    into it — landing on the Domain's home VIEW (`&sub=<viewSlug>`) with the
  //    object focused (the §2.5 canonical deep-link form). Live engine candidates
  //    (intel:/attention:/quickwin:…) have no commitment-kind prefix, so they
  //    never reach here — their absolute ctaHref (step 1) is the destination.
  const kind = t?.kind ?? PREFIX_KIND[prefix];
  const uid = t?.uid ?? extractUid(action.key) ?? extractUid(action.ctaHref);
  if (kind) {
    const domain = domainForKind(kind);
    if (domain) {
      const view = domain.views[0]?.slug;
      const home = view ? `${domainHomeRoute(domain)}&sub=${view}` : domainHomeRoute(domain);
      return uid ? `${home}#focus-${uid}` : home;
    }
  }
  // 3. A bare object reference → its object page.
  if (uid) return `/o/${uid}`;
  // 4. Last resort — keep any ctaHref, else the Work home (never a dead end).
  return action.ctaHref && action.ctaHref.startsWith("/") ? action.ctaHref : "/work";
}

/**
 * The object KIND an action is about — `target.kind` (engine-set) else the key
 * prefix via PREFIX_KIND (`crm_lead:<uid>` → `lead`). Null for the live engine
 * nudges keyed `onboarding:`/`intel:`/`attention:`/`quickwin:`/`section:` that
 * carry no commitment kind. The same derivation resolvePlayRoute uses — kept in
 * one place so scope and routing never disagree.
 */
export function playObjectKind(action: PlayRouteInput): string | null {
  if (action.target?.kind) return action.target.kind;
  const prefix = (action.key.split(":")[0] ?? "").toLowerCase();
  return PREFIX_KIND[prefix] ?? null;
}

/** The leading mode segment of an in-app href — workspace `/customers/…` or
 *  scoped `/projects/<p>/customers/…`. A mode-nested object link
 *  (`/customers/o/<uid>`) correctly yields its mode; only the canonical,
 *  mode-agnostic `/o/<uid>` (and subsystems like `/integrations`) returns null, so
 *  those never scope INTO a mode. */
export function modeFromHref(href: string | undefined | null): ModeSlug | null {
  if (!href || !href.startsWith("/")) return null;
  const segs = (href.split("?")[0] ?? "").split("/").filter(Boolean);
  const seg = segs[0] === "projects" && segs.length >= 3 ? segs[2] : segs[0];
  return seg && (MODE_SLUGS as readonly string[]).includes(seg) ? (seg as ModeSlug) : null;
}

/**
 * The mode a Play action BELONGS to (§2.4 surface scope): its object kind's home
 * mode (`modeForKind`), else the mode its Do-route lands in (`ctaHref`/deepLink).
 * Null ⇒ no mode home — a global setup nudge or subsystem link, which must NOT
 * appear on a mode-scoped strip. This is the predicate that was "advisory in P0".
 */
export function playHomeMode(action: PlayRouteInput): ModeSlug | null {
  const kind = playObjectKind(action);
  const byKind = kind ? modeForKind(kind) : null;
  if (byKind) return byKind;
  return modeFromHref(action.target?.deepLink ?? action.ctaHref);
}

/**
 * Does this action reference a concrete object — so the §2.5 Dive-in verb is
 * meaningful (it opens that object in its owning Domain)? Answerable cards and
 * pure engine nudges have no object ⇒ Dive-in is hidden (§2.3 verb availability).
 */
export function playHasObject(action: PlayRouteInput): boolean {
  if (action.target?.uid || action.target?.deepLink) return true;
  const prefix = (action.key.split(":")[0] ?? "").toLowerCase();
  if (PREFIX_KIND[prefix]) return true;
  return Boolean(extractUid(action.key) ?? extractUid(action.ctaHref));
}
